import { useCallback, useContext, useMemo } from "react";

import { Table, ConfigProvider, Tooltip, Typography, Tag, Flex } from "antd";

import CheckIcon from "@/icons/CheckIcon";
import ExclamationCircleIcon from "@/icons/ExclamationCircleIcon";
import LockCloseIcon from "@/icons/LockClosedIcon";
import PencilAltIcon from "@/icons/PencilAltIcon";
import RefreshIcon from "@/icons/RefreshIcon";

import EmployeeProfile from "@/components/cards/EmployeeProfile";
import ContextMenu from "@/components/containers/ContextMenu";
import CustomLink from "@/components/ui/CustomLink";
import ExpandableText from "@/components/views/ObjectFieldValue/ExpandableText";

import { UserContext } from "@/context/UserContext";
import MainContext from "@/layouts/MainLayout/context";

import { useColumnConfig } from "@/hooks/useColumnConfig";

import routes from "@/routes";
import {
  formateDate,
  formateISODateToMoment,
  getPluralizedWord,
} from "@/utils";
import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  TASK_TYPE_OPTIONS,
  TASK_FIELD_LABELS,
} from "@/constants/tasks";

const LOCAL_STORAGE_KEY = "relatedTasksTableColumnParameters";

const toFloorNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const num = Number(String(value).replace(",", "."));
  if (!Number.isFinite(num)) return null;

  return Math.floor(num);
};

const getDateValue = (value) => {
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : 0;
};

const getTextValue = (value) => String(value || "");

const getLookupItem = (items, uuid, embedded) =>
  embedded || items.find((item) => item.uuid === uuid) || null;

const isTaskCreatedFromTable = (task) => task?.created_from_table === true;

const renderTaskLinkText = (text) => (
  <Tooltip placement="bottomLeft" title={text}>
    <Typography.Paragraph
      ellipsis={{ rows: 2 }}
      style={{
        color: "inherit",
        lineHeight: 1.4,
        marginBottom: 0,
        display: "-webkit-inline-box",
      }}
    >
      {text}
    </Typography.Paragraph>
  </Tooltip>
);

const renderCosts = (costs) => {
  const value = Number(costs) || 0;

  if (value <= 0) return null;

  return (
    <Tooltip
      placement="bottomLeft"
      title={`${value} ${getPluralizedWord(value, ["час", "часа", "часов"])}`}
    >
      {`${toFloorNumber(value)} ${getPluralizedWord(toFloorNumber(value), [
        "час",
        "часа",
        "часов",
      ])}`}
    </Tooltip>
  );
};

// Таблица связанных задач
// Содержит список задач, которые ссылаются на текущий объект с возможностью сортировки по полям
const RelatedTasksTable = ({
  data,
  unit_uuid,
  theme = null,
  isAdmins = false,
  selectedRowKeys = [],
  onRowsSelect,
  statuses = [],
  priorities = [],
  resize = true,
  loading = false,
  className,
  onSyncTableTask,
  syncingTaskUuids = [],
}) => {
  const { user } = useContext(UserContext);
  const { config } = useContext(MainContext);

  const sprintLabel = config?.entities?.sprint?.name?.singular || "Спринт";

  const columnLabels = useMemo(
    () => ({
      ...TASK_FIELD_LABELS,
      sprint_uuid: sprintLabel,
    }),
    [sprintLabel],
  );

  const getHref = useCallback(
    (itemUuid, itemUnitUuid) =>
      unit_uuid
        ? routes.unitTaskDetail(unit_uuid, itemUuid)
        : isAdmins
          ? routes.administration.taskDetail(itemUuid)
          : itemUnitUuid
            ? routes.unitTaskDetail(itemUnitUuid, itemUuid)
            : routes.taskDetail(itemUuid),
    [isAdmins, unit_uuid],
  );

  const baseColumns = useMemo(
    () => [
      {
        title: columnLabels.task_number,
        dataIndex: "task_number",
        key: "task_number",
        width: 60,
        fixed: "left",
        sorter: {
          compare: (a, b) => (a.task_number || 0) - (b.task_number || 0),
        },
      },
      {
        title: columnLabels.name,
        dataIndex: "name",
        key: "name",
        width: 300,
        fixed: "left",
        sorter: {
          compare: (a, b) => getTextValue(a.name).localeCompare(b.name || ""),
        },
        render: (name, item) => (
          <CustomLink to={getHref(item.uuid, item.unit_uuid)} color="#303030">
            {renderTaskLinkText(name)}
          </CustomLink>
        ),
      },
      {
        title: <PencilAltIcon style={{ fontSize: 14, color: "#BBBBBB" }} />,
        dataIndex: "readOnly",
        key: "readOnly",
        width: 50,
        align: "center",
        render: (readOnly) =>
          readOnly ? (
            <LockCloseIcon
              style={{
                fontSize: 18,
                color: "#BBBBBB",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <CheckIcon
              style={{
                fontSize: 18,
                color: "#BBBBBB",
                verticalAlign: "middle",
              }}
            />
          ),
      },
      {
        title: "Создана автоматически",
        dataIndex: "created_from_table",
        key: "created_from_table",
        width: 150,
        align: "center",
        sorter: {
          compare: (a, b) =>
            Number(isTaskCreatedFromTable(a)) -
            Number(isTaskCreatedFromTable(b)),
        },
        render: (_, task) =>
          isTaskCreatedFromTable(task) ? (
            <CheckIcon
              style={{
                fontSize: 18,
                color: "#BBBBBB",
                verticalAlign: "middle",
              }}
            />
          ) : null,
      },
      {
        title: columnLabels.created_at,
        width: 150,
        dataIndex: "created_at",
        key: "created_at",
        sorter: {
          compare: (a, b) =>
            getDateValue(a.created_at) - getDateValue(b.created_at),
        },
        render: (created_at) => formateISODateToMoment(created_at),
      },
      {
        title: columnLabels.importance_uuid,
        width: 110,
        dataIndex: "importance_uuid",
        key: "importance_uuid",
        sorter: {
          compare: (a, b) =>
            getTextValue(a.importance?.name).localeCompare(
              b.importance?.name || "",
            ),
        },
        render: (importance_uuid, record) => {
          const priority = getLookupItem(
            priorities,
            importance_uuid,
            record.importance,
          );
          if (!priority) return null;

          const icon = PRIORITY_OPTIONS.find(
            (item) => item.value === priority.color,
          )?.icon;

          return (
            <Tag icon={icon} className="tag-with-icon">
              {priority.name}
            </Tag>
          );
        },
      },
      {
        title: columnLabels.status_uuid,
        width: 150,
        dataIndex: "status_uuid",
        key: "status_uuid",
        sorter: {
          compare: (a, b) =>
            getTextValue(a.status?.name).localeCompare(b.status?.name || ""),
        },
        render: (status_uuid, record) => {
          const status = getLookupItem(statuses, status_uuid, record.status);
          if (!status) return null;

          const icon = STATUS_OPTIONS.find(
            (item) => item.value === status.color,
          )?.icon;

          return (
            <Tag
              icon={icon}
              color={status.color}
              className="tag-with-icon-color"
            >
              {status.name}
            </Tag>
          );
        },
      },
      {
        title: columnLabels.description,
        dataIndex: "description",
        key: "description",
        width: 200,
        render: (value) => <ExpandableText value={value} line={2} markdown />,
      },
      {
        title: columnLabels.task_type_uuid,
        width: 150,
        dataIndex: "task_type_uuid",
        key: "task_type_uuid",
        sorter: {
          compare: (a, b) =>
            getTextValue(a.type?.name).localeCompare(b.type?.name || ""),
        },
        render: (_, record) => {
          const taskType = record.type || {};
          if (!taskType.name) return null;

          const icon = TASK_TYPE_OPTIONS.find(
            (item) => item.value === taskType.style_key,
          )?.icon;

          return (
            <Tag icon={icon} className="tag-with-icon lg">
              {taskType.name}
            </Tag>
          );
        },
      },
      {
        title: columnLabels.sprint_uuid,
        width: 150,
        dataIndex: "sprint_uuid",
        key: "sprint_uuid",
        sorter: {
          compare: (a, b) =>
            getTextValue(a.sprint?.name).localeCompare(b.sprint?.name || ""),
        },
        render: (_, record) => {
          const sprint = record.sprint;
          if (!sprint) return null;

          return (
            <CustomLink
              to={
                isAdmins
                  ? routes.administration.sprintDetail(sprint.uuid)
                  : routes.sprintDetail(sprint.uuid)
              }
              color="#303030"
            >
              {renderTaskLinkText(sprint.name)}
            </CustomLink>
          );
        },
      },
      {
        title: columnLabels.author,
        dataIndex: "author",
        key: "author",
        width: 180,
        ellipsis: { showTitle: false },
        sorter: {
          compare: (a, b) =>
            getTextValue(a.author?.fullname).localeCompare(
              b.author?.fullname || "",
            ),
        },
        render: (author) =>
          author ? (
            <EmployeeProfile
              {...author}
              fullName={false}
              avatarSize={24}
              tooltip
            />
          ) : null,
      },
      {
        title: columnLabels.executor,
        dataIndex: "executor",
        key: "executor",
        width: 180,
        ellipsis: { showTitle: false },
        sorter: {
          compare: (a, b) =>
            getTextValue(a.executor?.fullname).localeCompare(
              b.executor?.fullname || "",
            ),
        },
        render: (executor) =>
          executor ? (
            <EmployeeProfile
              {...executor}
              fullName={false}
              avatarSize={24}
              tooltip
            />
          ) : null,
      },
      {
        title: columnLabels.assignee_acceptor,
        dataIndex: "assignee_acceptor",
        key: "assignee_acceptor",
        width: 180,
        ellipsis: { showTitle: false },
        sorter: {
          compare: (a, b) =>
            getTextValue(a.assignee_acceptor?.fullname).localeCompare(
              b.assignee_acceptor?.fullname || "",
            ),
        },
        render: (assignee_acceptor) =>
          assignee_acceptor ? (
            <EmployeeProfile
              {...assignee_acceptor}
              fullName={false}
              avatarSize={24}
              tooltip
            />
          ) : null,
      },
      {
        title: columnLabels.parent_task_number,
        dataIndex: "parent",
        key: "parent_task_number",
        width: 120,
        sorter: {
          compare: (a, b) =>
            (a.parent?.task_number || 0) - (b.parent?.task_number || 0),
        },
        render: (parent, record) =>
          parent?.task_number && record.parent_task_uuid
            ? parent.task_number
            : null,
      },
      {
        title: columnLabels.parent_task_name,
        dataIndex: "parent",
        key: "parent_task_name",
        width: 250,
        sorter: {
          compare: (a, b) =>
            getTextValue(a.parent?.name).localeCompare(b.parent?.name || ""),
        },
        render: (parent, record) =>
          parent?.task_number && record.parent_task_uuid ? (
            <CustomLink
              to={getHref(record.parent_task_uuid, parent.unit_uuid)}
              color="#303030"
            >
              {renderTaskLinkText(parent.name)}
            </CustomLink>
          ) : null,
      },
      {
        title: columnLabels.start_date,
        width: 110,
        dataIndex: "start_date",
        key: "start_date",
        sorter: {
          compare: (a, b) =>
            getDateValue(a.start_date) - getDateValue(b.start_date),
        },
        render: (start_date) => (start_date ? formateDate(start_date) : null),
      },
      {
        title: columnLabels.due_date,
        width: 150,
        dataIndex: "due_date",
        key: "due_date",
        sorter: {
          compare: (a, b) =>
            getDateValue(a.due_date) - getDateValue(b.due_date),
        },
        render: (due_date) => {
          if (!due_date) return null;

          const isOverdue = new Date() >= new Date(due_date);

          return (
            <Flex align="center" gap={4}>
              {isOverdue && (
                <ExclamationCircleIcon
                  style={{ fontSize: 14, color: "#E66A25" }}
                />
              )}
              <span>{formateISODateToMoment(due_date)}</span>
            </Flex>
          );
        },
      },
      {
        title: columnLabels.planned_costs,
        width: 150,
        dataIndex: "planned_costs",
        key: "planned_costs",
        ellipsis: { showTitle: false },
        sorter: {
          compare: (a, b) =>
            (Number(a.planned_costs) || 0) - (Number(b.planned_costs) || 0),
        },
        render: renderCosts,
      },
      {
        title: columnLabels.actual_costs,
        width: 150,
        dataIndex: "actual_costs",
        key: "actual_costs",
        ellipsis: { showTitle: false },
        sorter: {
          compare: (a, b) => {
            const aTotal =
              (Number(a.actual_costs) || 0) +
              (Number(a.sum_children_actual_costs) || 0);
            const bTotal =
              (Number(b.actual_costs) || 0) +
              (Number(b.sum_children_actual_costs) || 0);

            return aTotal - bTotal;
          },
        },
        render: (costs, task) =>
          renderCosts(
            (Number(costs) || 0) + (Number(task.sum_children_actual_costs) || 0),
          ),
      },
      {
        title: columnLabels.unit,
        width: 150,
        dataIndex: "unit",
        key: "unit",
        ellipsis: { showTitle: false },
        sorter: {
          compare: (a, b) =>
            getTextValue(a.unit?.name).localeCompare(b.unit?.name || ""),
        },
        render: (unit) => (
          <Tooltip placement="bottomLeft" title={unit?.name}>
            {unit?.name}
          </Tooltip>
        ),
      },
      {
        title: "",
        width: 48,
        align: "center",
        fixed: "right",
        key: "actions",
        render: (_, task) => {
          if (!onSyncTableTask || !isTaskCreatedFromTable(task)) return null;

          const isSyncing = syncingTaskUuids.includes(task.uuid);

          return (
            <ContextMenu
              vertical
              items={[
                {
                  key: "sync_table_task",
                  disabled: isSyncing,
                  onClick: () => onSyncTableTask(task),
                  label: (
                    <Flex align="center" gap={10}>
                      <RefreshIcon
                        style={{
                          fontSize: 16,
                          color: "#e66a25",
                        }}
                      />
                      Обновить задачу
                    </Flex>
                  ),
                },
              ]}
            />
          );
        },
      },
    ],
    [
      columnLabels,
      getHref,
      isAdmins,
      onSyncTableTask,
      priorities,
      statuses,
      syncingTaskUuids,
    ],
  );

  const { columns, components } = useColumnConfig(
    baseColumns,
    user.user_uuid,
    LOCAL_STORAGE_KEY,
    {
      resize,
      columnLabels,
    },
  );

  const isPassport = theme === "passport";

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            borderColor: isPassport ? "#E9E9E9" : "#f7f7f7",
          },
        },
      }}
    >
      <Table
        className={className}
        dataSource={data}
        columns={columns}
        rowKey="uuid"
        loading={loading}
        pagination={false}
        rowSelection={
          onRowsSelect
            ? {
                selectedRowKeys,
                preserveSelectedRowKeys: true,
                fixed: true,
                onChange: onRowsSelect,
              }
            : undefined
        }
        components={resize ? components : undefined}
        showSorterTooltip={{
          target: "sorter-icon",
          title: "Нажмите для сортировки",
        }}
        size="small"
        scroll={{
          x: 2908,
        }}
        tableLayout="fixed"
        bordered={isPassport}
      />
    </ConfigProvider>
  );
};

export default RelatedTasksTable;
